// Pure helpers for the Draw screen (card d76b3080): the running order of each
// round, where each round starts, the dance groups (card 993c6c6c) and the
// checks shown before a draw is locked. No React, no network.

// A competition's draw status from a /draws dashboard row.
export const statusOf = (row) => (!row?.draw ? 'none' : row.draw.locked ? 'locked' : 'draft');

export const STATUS_LABEL = { none: 'Not drawn', draft: 'Draft', locked: 'Locked' };

// The order rotated to start at competitor number `start` (wrapping), the way
// every round after the first is danced. Matches by number, like the backend.
export function rotateTo(order, start) {
  const n = order.length;
  if (!n) return [];
  let s = order.findIndex((o) => Number(o.number) === Number(start));
  if (s < 0) s = 0;
  return order.slice(s).concat(order.slice(0, s));
}

export function roundOrder(draw, round) {
  if (!draw) return [];
  const start = (draw.roundStarts || [])[round - 1];
  return start == null ? draw.order : rotateTo(draw.order, start);
}

const groupKey = (row) => row.groupKey || '';

// The published list order (Devin 2026-10-19): the draw starting at its seeded
// list start, so round 1 (number 101) sits somewhere down the list and nobody
// can count numbers from the top. Older draws have no listStart: number order.
export function listOrder(draw) {
  if (!draw) return [];
  const byNumber = (draw.order || []).slice().sort((a, b) => a.number - b.number);
  return draw.listStart == null ? byNumber : rotateTo(byNumber, draw.listStart);
}

// The display list for a view: view 0 = the draw as published (listOrder) with
// a marker where each round starts; view k = round k's running order. Dance group
// headings show where the groups matter: every view for a one-round set
// competition, and round `grouping.round` (plus the draw order) otherwise.
//   [{ kind: 'start', round } | { kind: 'group', key } | { kind: 'row', row }]
export function viewItems(draw, view) {
  if (!draw) return [];
  const rows = view === 0 ? listOrder(draw) : roundOrder(draw, view);
  const grouping = draw.grouping || null;
  const showGroups = !!grouping && (view === 0 || view === grouping.round);
  const startAt = new Map();
  if (view === 0) {
    (draw.roundStarts || []).forEach((num, i) => {
      if (!startAt.has(Number(num))) startAt.set(Number(num), []);
      startAt.get(Number(num)).push(i + 1);
    });
  }
  const items = [];
  let prev = null;
  rows.forEach((row) => {
    (startAt.get(Number(row.number)) || []).forEach((round) => items.push({ kind: 'start', round }));
    if (showGroups && (prev === null || groupKey(row) !== groupKey(prev) || startAt.has(Number(row.number)))) {
      items.push({ kind: 'group', key: groupKey(row) });
    }
    items.push({ kind: 'row', row });
    prev = row;
  });
  return items;
}

// Classmates closer than the draw's two-dancer gap (Devin 2026-10-19:
// classmates never on stage together), as [a, b, places apart]. With more
// than one round every later round is a rotation of the draw, so the order is
// a circle (the last dancer and the first are neighbours); a one-round
// competition is the round 1 line. Pass the order in the danced sequence.
export function classmatesTooClose(order, circular = true, gap = 2) {
  const rows = order || [];
  const n = rows.length;
  const pairs = [];
  for (let a = 0; a < n; a++) {
    for (let b = a + 1; b < n; b++) {
      const apart = circular ? Math.min(b - a, n - (b - a)) : b - a;
      if (apart > gap || !rows[a].schoolKey || rows[a].schoolKey !== rows[b].schoolKey) continue;
      pairs.push(apart === b - a ? [rows[a], rows[b], apart] : [rows[b], rows[a], apart]);
    }
  }
  return pairs.sort((x, y) => x[2] - y[2]);
}

// Each dance group danced together, in the grouped round? (The backend draws
// it that way; this is the visible check before locking.)
export function groupsTogether(draw) {
  if (!draw?.grouping) return true;
  const rows = roundOrder(draw, draw.grouping.round);
  const seen = new Set();
  let prev = null;
  for (const row of rows) {
    const k = groupKey(row);
    if (k !== prev) {
      if (seen.has(k)) return false;
      seen.add(k);
      prev = k;
    }
  }
  return true;
}

const ROUND_DANCE_NAMES = { slip_jig: 'Slip Jig', reel: 'Reel', light_jig: 'Light Jig', slow_treble_jig: 'Slow Treble Jig' };

const prettyKey = (key) =>
  String(key || '')
    .split('_')
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');

export function setName(key, setNames) {
  return (setNames && setNames[key]) || prettyKey(key);
}

// Heading text for a dance group.
export function groupLabel(grouping, key, setNames) {
  if (!grouping) return '';
  return grouping.by === 'roundDance' ? ROUND_DANCE_NAMES[key] || prettyKey(key) : setName(key, setNames);
}

// The chosen-dance columns this competition has: the round dance (labelled by
// its round) and the set (with its speed for championships, where it is the
// recall set; one-round set competitions show the set only).
export function choiceColumns(competition, draw) {
  const rows = draw?.order || [];
  const cols = [];
  if (rows.some((r) => r.roundDance)) {
    const round = competition?.roundDanceRound || draw?.grouping?.round;
    cols.push({ key: 'roundDance', label: round ? `Round ${round} dance` : 'Round dance' });
  }
  if (rows.some((r) => r.danceChoice)) {
    const rounds = Number(competition?.rounds || draw?.rounds) || 1;
    cols.push({ key: 'set', label: rounds > 1 ? 'Recall set' : 'Set', withSpeed: rounds > 1 });
  }
  return cols;
}

export function choiceText(col, row, setNames) {
  if (col.key === 'roundDance') return row.roundDanceName || ROUND_DANCE_NAMES[row.roundDance] || '';
  if (col.key === 'set') {
    if (!row.danceChoice) return '';
    const name = setName(row.danceChoice, setNames);
    if (!col.withSpeed || !row.speed) return name;
    return `${name} · ${row.speed === 'traditional' ? 'trad. speed' : row.speed}`;
  }
  return '';
}

// The program list (Devin 2026-10-19): names are printed in the program and
// shown in the app in the published list order (listOrder) WITHOUT numbers —
// numbers are released the morning of the event so they can't be passed to
// judges. From the /draws/export rows; only locked draws are included.
//   → { rows: [{ code, competition, dancer, school }], notLocked: [codes] }
export function programList(exportRows) {
  const rows = [];
  const notLocked = [];
  (exportRows || []).forEach((r) => {
    if (!r.draw) {
      if (r.entrantCount > 0) notLocked.push(r.competition.code);
      return;
    }
    if (!r.draw.locked) {
      notLocked.push(r.competition.code);
      return;
    }
    const order = listOrder(r.draw);
    order.forEach((o) =>
      rows.push({
        code: r.competition.code,
        competition: r.competition.name,
        dancer: o.label || o.schoolName || '',
        school: o.schoolName || '',
      })
    );
  });
  return { rows, notLocked };
}

// "Grouped by round 2 dance" / "Grouped by set" for the list and header.
export function groupingTag(grouping) {
  if (!grouping) return '';
  return grouping.by === 'roundDance' ? `Grouped by round ${grouping.round} dance` : 'Grouped by set';
}
